// Render-time gate for PAID cosmetics: they only show for a signed-in player.
// Equips live in local storage (account-independent), so signing out must hide
// them without deleting them - sign back in and everything is equipped exactly
// as it was. The auth provider flips this as the session resolves. The default
// is "allowed" so a signed-in player's cosmetics never flicker off during the
// auth handshake; a signed-out visitor instead gets a moment of grace before
// the gate closes, which is invisible in practice (menus render again).
//
// Free cosmetics (country flags) pass through untouched; only items the shop
// actually sells (isPurchasable) are gated. Online matches are already policed
// server-side (the seat-identity check strips unowned cosmetics); this gate is
// what keeps LOCAL rendering honest.
import { isPurchasable } from "./shop/store";

let allowed = true;
type Listener = () => void;
const listeners = new Set<Listener>();

export function setPaidCosmeticsAllowed(v: boolean): void {
  if (allowed === v) return;
  allowed = v;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* a misbehaving listener must not break the rest */
    }
  });
}

export function paidCosmeticsAllowed(): boolean {
  return allowed;
}

// The one helper readers use: an id passes through unless it's a purchasable
// cosmetic and the gate is closed.
export function gateCosmetic(kind: string, id: string | null | undefined): string | null {
  if (!id) return null;
  if (!allowed && isPurchasable(kind, id)) return null;
  return id;
}

// Stores that cache or notify on cosmetic reads subscribe so their own
// listeners re-read when the gate flips (e.g. signing out on an open screen).
export function subscribeCosmeticsGate(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
